import * as React from 'react';
import { useFormContext } from 'react-hook-form';
import ePub from 'epubjs';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist/build/pdf';
//@ts-ignore
import pdfWorker from 'pdfjs-dist/build/pdf.worker.js?url';

import UploadBookThumbnail from './Thumbnail/UploadBookThumbnail';
import CreateThumbnail from './Thumbnail/CreateTextThumbnail';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@readcloud/ui/tabs';
import { FormMessage } from '@readcloud/ui/form';
import { LoadingIcon } from '../LoadingIcon/LoadingIcon';
import { AnimatedMount } from '../AnimatedMount';
import { UploadImageThumbnail } from './Thumbnail/UploadImageThumbnail';
import type { MimeTypes } from './types/mimeTypes';
import { contentTypeConfig } from './schemas/uploadWizardConfig';
import { ThumbnailFieldData } from './schemas/formFields';
import { tauriFetch } from '@readcloud/fetch-wrapper';
// Set the workerSrc to the path of the PDF.js worker
GlobalWorkerOptions.workerSrc = pdfWorker;

interface IUploadThumbnailProps {
  type: keyof typeof contentTypeConfig;
  setFileData: (data: { type: MimeTypes; name: string }) => void;
  fileData?: {
    type: MimeTypes;
    name: string;
  };

  file?: File | null;
  url?: string | null;
}

const UploadThumbnail: React.FunctionComponent<IUploadThumbnailProps> = ({
  setFileData,
  fileData,
  file,
  url,
  type,
}) => {
  const { formState, setValue } = useFormContext<ThumbnailFieldData>();
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [pdfDocument, setPdfDocument] = React.useState<any>(null);
  const [totalPages, setTotalPages] = React.useState(0);
  const [thumbnailUrl, setThumbnailUrl] = React.useState<string | null>(null);

  const saveThumbnail = React.useCallback(
    (data: string) => {
      setValue('thumbnail', data, { shouldValidate: false });
    },
    [setValue]
  );

  const fetchFileAsArrayBuffer = async (
    file: File | string
  ): Promise<ArrayBuffer> => {
    if (typeof file === 'string') {
      const response = await tauriFetch(file);
      return await response.arrayBuffer();
    } else {
      return await new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(file);
      });
    }
  };

  const fetchFileAsBase64 = async (file: File | string): Promise<string> => {
    if (typeof file === 'string') {
      const response = await tauriFetch(file);
      const base64String = await response.text();
      return base64String;
    } else {
      return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(file);
      });
    }
  };

  const processArrayBuffer = async (
    arrayBuffer: ArrayBuffer,
    fileType: string
  ) => {
    try {
      if (fileType === 'application/pdf') {
        const typedArray = new Uint8Array(arrayBuffer);
        const pdf = await getDocument(typedArray).promise;
        setPdfDocument(pdf);
        setTotalPages(pdf.numPages);
      } else if (fileType === 'application/epub+zip') {
        const blob = new Blob([arrayBuffer], { type: 'application/epub+zip' });
        await getEpubCoverThumbnail(blob);
      } else {
        setError('Content preview not supported');
      }
    } catch (error) {
      console.error('Error processing document:', error);
      setError('Failed to process document');
    }
  };

  const processURL = async (url: string, fileType: string) => {
    try {
      if (fileType === 'application/pdf') {
        const pdf = await getDocument(url).promise;
        setPdfDocument(pdf);
        setTotalPages(pdf.numPages);
      } else if (fileType === 'application/epub+zip') {
        await getEpubCoverThumbnail(url);
      } else if (fileType.startsWith('image/')) {
        setError('Image preview not supported when added via URL');
      } else {
        setError('Content preview not supported');
      }
    } catch (error) {
      console.error('Error processing document:', error);
      setError('Failed to process document');
    }
  };

  React.useEffect(() => {
    const processFileOrUrl = async () => {
      try {
        setLoading(true);
        setError(null);
        if (file) {
          const fileType = file.type as MimeTypes;
          setFileData({ type: fileType, name: file.name });

          if (
            fileType === 'application/pdf' ||
            fileType === 'application/epub+zip'
          ) {
            const arrayBuffer = await fetchFileAsArrayBuffer(file);
            await processArrayBuffer(arrayBuffer, fileType);
          } else if (fileType.startsWith('image/')) {
            const base64String = await fetchFileAsBase64(file);
            setThumbnailUrl(base64String);
          } else {
            setError('Content preview not supported');
          }
        } else if (url) {
          const fileName = url.substring(url.lastIndexOf('/') + 1);
          const fileType = url.endsWith('.pdf')
            ? 'application/pdf'
            : 'application/epub+zip';
          setFileData({ type: fileType, name: fileName });

          await processURL(url, fileType);
        }
      } catch (error) {
        console.error('Error processing file or URL:', error);
        setError('Failed to load document');
      } finally {
        setLoading(false);
      }
    };

    if (file || url) {
      processFileOrUrl();
    }
  }, [file, url]);

  const getEpubCoverThumbnail = async (file: Blob | string) => {
    try {
      const epubBook = ePub(file as any);

      epubBook.ready
        .then(() => {
          if (epubBook.cover) {
            if (epubBook.archived) {
              epubBook.archive
                .createUrl(epubBook.cover, { base64: true })
                .then((coverUrl: string) => {
                  if (coverUrl) {
                    setThumbnailUrl(coverUrl);
                  }
                })
                .catch((error) => {
                  console.error('Error creating EPUB cover URL:', error);
                  setError('Failed to generate thumbnail');
                });
            } else {
              setThumbnailUrl(epubBook.cover);
            }
          } else {
            setError('Failed to generate thumbnail');
          }
        })
        .catch((error) => {
          console.error('Error loading EPUB:', error);
          setError('Failed to generate thumbnail');
        });
    } catch (error) {
      console.error('Error processing EPUB file:', error);
      setError('Failed to generate thumbnail');
    }
  };

  const thumbnailOptions = type
    ? contentTypeConfig[type]?.uploadThumbnail
    : null;
  const enabledOptions = thumbnailOptions
    ? Object.entries(thumbnailOptions)
        .filter(([_, config]) => config.enabled)
        .map(([key, config]) => ({
          value: key as string,
          label: config.label || key,
          description: config.description,
        }))
    : [];

  const defaultValue =
    enabledOptions.length > 0 ? enabledOptions[0].value : undefined;

  return (
    <div className="rc-flex rc-flex-col rc-justify-center rc-items-center">
      {thumbnailOptions && (
        <Tabs defaultValue={defaultValue} className="rc-w-full rc-space-y-6">
          <TabsList className="rc-w-full rc-flex">
            {enabledOptions.map((option) => (
              <TabsTrigger
                key={option.value}
                value={option.value}
                className="rc-flex-1"
                title={option.description}
              >
                {option.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {thumbnailOptions.fromContent?.enabled && (
            <TabsContent value="fromContent">
              <div className="rc-flex rc-flex-col rc-items-center rc-justify-center rc-w-full">
                <AnimatedMount show={loading}>
                  <LoadingIcon loading />
                </AnimatedMount>
                <AnimatedMount show={!!error}>
                  <div>{error}</div>
                </AnimatedMount>
                {!loading && !error && fileData && (
                  <UploadBookThumbnail
                    fileData={fileData}
                    pdfDocument={pdfDocument}
                    totalPages={totalPages}
                    thumbnailUrl={thumbnailUrl}
                    onThumbnailSelect={saveThumbnail}
                  />
                )}
              </div>
            </TabsContent>
          )}

          {thumbnailOptions.create?.enabled && (
            <TabsContent value="create">
              <CreateThumbnail
                onThumbnailCreate={saveThumbnail}
                fileData={fileData}
              />
            </TabsContent>
          )}

          {thumbnailOptions.upload?.enabled && (
            <TabsContent value="upload">
              <UploadImageThumbnail onThumbnailUpload={saveThumbnail} />
            </TabsContent>
          )}
        </Tabs>
      )}
      <FormMessage className="rc-mt-2">
        {formState.errors.thumbnail && formState.errors.thumbnail.message}
      </FormMessage>
    </div>
  );
};

export default UploadThumbnail;
